import { createSlice } from '@reduxjs/toolkit';
import {
  isFieldChat,
  isDocumentChat,
  isFundChat,
  isTopLevelChat,
} from './chatTopicType';
import AssistantMessageStatus from './assistantMessageStatus';

/**
{
  documentChatsByFundId: {
    "fund_1": {
      "document_1": {
        "chatHistory": [
          {
            "role": "user",
            "content": "Hello, how are you?"
          },
          {
            "role": "assistant",
            "status": "PENDING" || "COMPLETED" || "ERROR",
            "content": "I'm good, thank you!"
          }
        ],
        "additionalSelectedDocs": [
          "doc_path_1",
          "doc_path_2",
        ]
      }
    }
  },
  fieldChatsByFundId: {
    "fundId1": {
      "fieldName1": {
        "chatHistory": [
          {
            "role": "user",
            "content": "Hello, how are you?"
          },
          {
            "role": "assistant",
            "status": "PENDING" || "COMPLETED" || "ERROR",
            "content": "I'm good, thank you!"
          }
        ],
        "additionalSelectedDocs": [
          "doc_path_1",
          "doc_path_2",
        ]
      }
    }
  },
  fundChatsByFundId: {
    "fund_1": {
      "chatHistory": [
          {
            "role": "user",
            "content": "Hello, how are you?"
          },
          {
            "role": "assistant",
            "status": "PENDING" || "COMPLETED" || "ERROR",
            "content": "I'm good, thank you!"
          }
        ]
      ],
    }
  }
}
*/

const initialState = {
  currentChatTopic: null,
  documentChatsByFundId: {},
  fieldChatsByFundId: {},
  fundChatsByFundId: {},
  topLevelChat: {
    chatHistory: [],
    additionalSelectedDocs: [],
  },
};

const addChatHistoriesForFundIdIfNotExists = (chatFundObject, fundId) => {
  if (!chatFundObject[fundId]) {
    chatFundObject[fundId] = {
      chatHistory: [],
      additionalSelectedDocs: [],
    };
  }
};

const addFieldChatHistoryIfNotExists = (chatFundObject, fieldName) => {
  if (!chatFundObject[fieldName]) {
    chatFundObject[fieldName] = {
      chatHistory: [],
      additionalSelectedDocs: [],
    };
  }
};

const addDocumentChatHistoryIfNotExists = (chatFundObject, documentPath) => {
  if (!chatFundObject[documentPath]) {
    chatFundObject[documentPath] = {
      chatHistory: [],
      additionalSelectedDocs: [],
    };
  }
};

const appendUserMessageToChatHistory = (chatObject, message) => {
  const chatHistory = chatObject?.chatHistory ?? [];
  const newChatHistory = [
    ...chatHistory,
    {
      role: 'user',
      content: message,
    },
  ];
  chatObject.chatHistory = newChatHistory;
};

const appendPendingAssistantMessageToChatHistory = (chatObject) => {
  const chatHistory = chatObject?.chatHistory ?? [];
  const newChatHistory = [
    ...chatHistory,
    {
      role: 'assistant',
      status: AssistantMessageStatus.PENDING,
      content: '',
    },
  ];
  chatObject.chatHistory = newChatHistory;
};

const getChatStateObjectForTopic = (state, topic) => {
  if (isFieldChat(topic)) {
    addChatHistoriesForFundIdIfNotExists(
      state.fieldChatsByFundId,
      topic.fundId
    );
    addFieldChatHistoryIfNotExists(
      state.fieldChatsByFundId[topic.fundId],
      topic.fieldName
    );
    return state.fieldChatsByFundId?.[topic.fundId]?.[topic.fieldName];
  } else if (isDocumentChat(topic)) {
    addChatHistoriesForFundIdIfNotExists(
      state.documentChatsByFundId,
      topic.fundId
    );
    addDocumentChatHistoryIfNotExists(
      state.documentChatsByFundId[topic.fundId],
      topic.document.id
    );
    return state.documentChatsByFundId?.[topic.fundId]?.[topic.document.id];
  } else if (isFundChat(topic)) {
    addChatHistoriesForFundIdIfNotExists(state.fundChatsByFundId, topic.fundId);
    return state.fundChatsByFundId?.[topic.fundId];
  } else if (isTopLevelChat(topic)) {
    return state.topLevelChat;
  }
};

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    submitUserMessage: (state, { payload: { topic, message } }) => {
      const chatStateObject = getChatStateObjectForTopic(state, topic);
      appendUserMessageToChatHistory(chatStateObject, message);
      appendPendingAssistantMessageToChatHistory(chatStateObject);
    },
    contributeChunkToLatestAssistantMessage: (
      state,
      { payload: { topic, chunk } }
    ) => {
      const chatStateObject = getChatStateObjectForTopic(state, topic);
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      latestAssistantMessage.content += chunk;
    },
    completeLatestAssistantMessage: (
      state,
      { payload: { topic, response = '' } }
    ) => {
      const chatStateObject = getChatStateObjectForTopic(state, topic);
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      if (response) {
        latestAssistantMessage.content = response;
      }
      latestAssistantMessage.status = AssistantMessageStatus.COMPLETED;
    },
    errorLatestAssistantMessage: (state, { payload: { topic } }) => {
      const chatStateObject = getChatStateObjectForTopic(state, topic);
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      latestAssistantMessage.status = AssistantMessageStatus.ERROR;
    },
    setAdditionalSelectedDocs: (state, { payload: { topic, docPaths } }) => {
      const chatStateObject = getChatStateObjectForTopic(state, topic);
      chatStateObject.additionalSelectedDocs = docPaths;
    },
    clearChatForTopic: (state, { payload: { topic } }) => {
      const chatStateObject = getChatStateObjectForTopic(state, topic);
      chatStateObject.chatHistory = [];
      chatStateObject.additionalSelectedDocs = [];
    },
    setCurrentChatTopic: (state, { payload }) => {
      state.currentChatTopic = payload;
    },
    clearCurrentChatTopic: (state) => {
      state.currentChatTopic = null;
    },
    addActionsToLatestAssistantMessage: (
      state,
      { payload: { topic, actions } }
    ) => {
      const chatStateObject = getChatStateObjectForTopic(state, topic);
      const latestAssistantMessage =
        chatStateObject.chatHistory[chatStateObject.chatHistory.length - 1];
      if (!latestAssistantMessage.actions) {
        latestAssistantMessage.actions = [];
      }
      latestAssistantMessage.actions.push(...actions);
    },
  },
});

export const {
  submitUserMessage,
  contributeChunkToLatestAssistantMessage,
  completeLatestAssistantMessage,
  errorLatestAssistantMessage,
  setAdditionalSelectedDocs,
  clearChatForTopic,
  setCurrentChatTopic,
  clearCurrentChatTopic,
  addActionsToLatestAssistantMessage,
} = aiChatSlice.actions;
export default aiChatSlice.reducer;
